import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';

import {
  DAILY,
  ENGINE_HOURS,
  FUEL_USED,
  IDLE_ENGINE_HOURS,
  IDLE_FUEL_USED,
  LIFETIME,
  MONTHLY,
  WEEKLY,
} from '../../Constants/Signal';

export function EngineUtilizationTextWidget({ data }) {
  const daily = useMemo(() => {
      const { [ENGINE_HOURS]: all, [IDLE_ENGINE_HOURS]: idle } =
        data?.get?.(DAILY) ?? {};

      const idle_total = idle?.max - idle?.min,
        total = all?.max - all?.min,
        percentage = ((total - idle_total) / total) * 100;

      return { percentage, total };
    }, [data]),
    lifetime = useMemo(() => {
      const { [ENGINE_HOURS]: all, [IDLE_ENGINE_HOURS]: idle } =
        data?.get?.(LIFETIME) ?? {};

      const idle_total = idle?.max - idle?.min,
        total = all?.max - all?.min,
        percentage = ((total - idle_total) / total) * 100;

      return { percentage, total };
    }, [data]),
    monthly = useMemo(() => {
      const { [ENGINE_HOURS]: all, [IDLE_ENGINE_HOURS]: idle } =
        data?.get?.(MONTHLY) ?? {};

      const idle_total = idle?.max - idle?.min,
        total = all?.max - all?.min,
        percentage = ((total - idle_total) / total) * 100;

      return { percentage, total };
    }, [data]),
    weekly = useMemo(() => {
      const { [ENGINE_HOURS]: all, [IDLE_ENGINE_HOURS]: idle } =
        data?.get?.(WEEKLY) ?? {};

      const idle_total = idle?.max - idle?.min,
        total = all?.max - all?.min,
        percentage = ((total - idle_total) / total) * 100;

      return { percentage, total };
    }, [data]);

  const formatNumber = useCallback((value) => {
    if (!Number.isFinite(value)) return 'not available';

    return Number.isInteger(value) ? `${value} Hrs` : `${value.toFixed(2)} Hrs`;
  }, []);
  const formatPercentage = useCallback((value) => {
    if (!Number.isFinite(value)) return 'not available';

    return Number.isInteger(value) ? `${value} %` : `${value.toFixed(2)} %`;
  }, []);
  return (
    <div className="h-100 w-100 px-3">
      <table className="table table-borderless table-hover table-responsive-sm h-100 text-secondary">
        <thead>
          <tr>
            <th scope="col" />
            <th className="text-nowrap text-center" scope="col">
              Engine Hours
            </th>
            <th className="text-nowrap text-center" scope="col">
              Engine Working %
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Lifetime</th>
            <td className="text-center">{formatNumber(lifetime?.total)}</td>
            <td className="text-center">
              {formatPercentage(lifetime?.percentage)}
            </td>
          </tr>
          <tr>
            <th scope="row">Today</th>
            <td className="text-center">{formatNumber(daily?.total)}</td>
            <td className="text-center">
              {formatPercentage(daily?.percentage)}
            </td>
          </tr>
          <tr>
            <th className="text-nowrap" scope="row">
              This Week
            </th>
            <td className="text-center">{formatNumber(weekly?.total)}</td>
            <td className="text-center">
              {formatPercentage(weekly?.percentage)}
            </td>
          </tr>
          <tr>
            <th className="text-nowrap" scope="row">
              This Month
            </th>
            <td className="text-center">{formatNumber(monthly?.total)}</td>
            <td className="text-center">
              {formatPercentage(monthly?.percentage)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

EngineUtilizationTextWidget.propTypes = { data: PropTypes.object };

export function FuelUtilizationTextWidget({ data }) {
  const daily = useMemo(() => {
      const { [FUEL_USED]: all, [IDLE_FUEL_USED]: idle } =
        data?.get?.(DAILY) ?? {};

      const idle_total = idle?.max - idle?.min,
        total = all?.max - all?.min,
        percentage = (idle_total / total) * 100;

      return { percentage, total };
    }, [data]),
    lifetime = useMemo(() => {
      const { [FUEL_USED]: all, [IDLE_FUEL_USED]: idle } =
        data?.get?.(LIFETIME) ?? {};

      const idle_total = idle?.max - idle?.min,
        total = all?.max - all?.min,
        percentage = ((total - idle_total) / total) * 100;

      return { percentage, total };
    }, [data]),
    monthly = useMemo(() => {
      const { [FUEL_USED]: all, [IDLE_FUEL_USED]: idle } =
        data?.get?.(MONTHLY) ?? {};

      const idle_total = idle?.max - idle?.min,
        total = all?.max - all?.min,
        percentage = ((total - idle_total) / total) * 100;

      return { percentage, total };
    }, [data]),
    weekly = useMemo(() => {
      const { [FUEL_USED]: all, [IDLE_FUEL_USED]: idle } =
        data?.get?.(WEEKLY) ?? {};

      const idle_total = idle?.max - idle?.min,
        total = all?.max - all?.min,
        percentage = ((total - idle_total) / total) * 100;

      return { percentage, total };
    }, [data]);

  const formatNumber = useCallback((value) => {
    if (!Number.isFinite(value)) return 'not available';

    return Number.isInteger(value) ? value : value.toFixed(2);
  }, []);
  const formatPercentage = useCallback((value) => {
    if (!Number.isFinite(value)) return 'not available';

    return Number.isInteger(value) ? `${value} %` : `${value.toFixed(2)} %`;
  }, []);
  return (
    <div className="h-100 w-100 px-3">
      <table className="table table-borderless table-hover table-responsive-sm h-100 text-secondary">
        <thead>
          <tr>
            <th scope="col" />
            <th className="text-nowrap text-center" scope="col">
              Fuel Used (gal/hr)
            </th>
            <th className="text-nowrap text-center" scope="col">
              Working Fuel %
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Lifetime</th>
            <td className="text-center">{formatNumber(lifetime?.total)}</td>
            <td className="text-center">
              {formatPercentage(lifetime?.percentage)}
            </td>
          </tr>
          <tr>
            <th scope="row">Today</th>
            <td className="text-center">{formatNumber(daily?.total)}</td>
            <td className="text-center">
              {formatPercentage(daily?.percentage)}
            </td>
          </tr>
          <tr>
            <th className="text-nowrap" scope="row">
              This Week
            </th>
            <td className="text-center">{formatNumber(weekly?.total)}</td>
            <td className="text-center">
              {formatPercentage(weekly?.percentage)}
            </td>
          </tr>
          <tr>
            <th className="text-nowrap" scope="row">
              This Month
            </th>
            <td className="text-center">{formatNumber(monthly?.total)}</td>
            <td className="text-center">
              {formatPercentage(monthly?.percentage)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

FuelUtilizationTextWidget.propTypes = { data: PropTypes.object };
